import { ResolverTag } from '@ps/web-core-enums';
import { Size as ModalSize } from '@ps/web-core-store';
import { useNavigate } from '@ps/web-shared-components';
import React from 'react';
import type { ComponentClass, FunctionComponent } from 'react';
import { connect } from 'react-redux';
import type { RouteComponentProps } from 'react-router';
import { compose } from 'redux';

import type { ModalContextValue } from '@swf/components/StarsModal/Context';
import { ModalContext } from '@swf/components/StarsModal/Context';
import withResolver from '@swf/core/hoc/withResolver';
import type { Dispatch, RootState } from '@swf/core/store';
import { dispatch, select } from '@swf/core/store';
import type { BaseMPNotification, MultiPurposeNotification } from '@swf/core/types/Notification';
import { c2lHandler } from '@swf/core/utils/c2l';

import Main from './components/Main/Main';
import type { CardConfig } from './config/NotificationTypes';
import NotificationTypesConfigs from './config/NotificationTypes';
import { NotificationType } from './enums';
import { parseQsNotificationData } from './utils';

type StateProps = ReturnType<typeof mapState>;
type DispatchProps = ReturnType<typeof mapDispatch>;
type Props = StateProps &
  DispatchProps &
  RouteComponentProps<object, object, BaseMPNotification> &
  NavigateProps;

interface ActionArgs {
  notificationType: NotificationType;
  closeFn(path?: string): void;
}

interface NavigateProps {
  navigate: (path: string) => void | undefined;
}

class StarsMPNotification extends React.PureComponent<Props> {
  static contextType = ModalContext;
  state = {
    showMoreInfo: false
  };

  constructor(props: Props, context: ModalContextValue) {
    super(props, context);

    const showAlternativeSize = [
      NotificationType.BonusBase,
      NotificationType.FreeSpinsBase
    ].includes(props.location?.state?.type?.split('.')[0] as unknown as NotificationType);

    context.setStyle({ size: showAlternativeSize ? ModalSize.MEDIUM : ModalSize.SMALL });
  }

  componentDidUpdate(prevProps: Props) {
    const {
      location: { pathname, state },
      history
    } = this.props;
    if (state !== prevProps.location.state) {
      history.push(pathname, state);
    }
  }

  getNotificationConfig = (notificationType: string): CardConfig => {
    const notificationConfig = NotificationTypesConfigs[
      notificationType as keyof typeof NotificationTypesConfigs
    ] as CardConfig;
    if (!notificationConfig) {
      const expectedConfigs = Object.keys(NotificationTypesConfigs).join(', ');
      throw new Error(
        `Unrecognised notificationType: ${notificationType}. One of [${expectedConfigs}] expected`
      );
    } else {
      return notificationConfig;
    }
  };

  onClickPrimaryAction = ({ notificationType, closeFn }: ActionArgs) => {
    if (notificationType.includes(NotificationType.FixedPrizeDrawBase)) {
      closeFn('/mpnotifications');
    }
  };

  onClickSecondaryAction = ({ notificationType, closeFn }: ActionArgs) => {
    if (notificationType.includes(NotificationType.FixedPrizeDrawBase)) {
      this.onToggleFlip();
    } else {
      closeFn('/mpnotifications');
    }
  };

  onToggleFlip = () => this.setState({ showMoreInfo: !this.state.showMoreInfo });

  render() {
    const { location, close, language, license, history, navigate } = this.props;
    const notificationData = location.state || parseQsNotificationData(location.search);
    const { type, notificationId } = notificationData;
    const { front, back } = this.getNotificationConfig(type);
    return (
      <Main
        cardData={notificationData as MultiPurposeNotification}
        front={front}
        back={back}
        showBack={this.state.showMoreInfo}
        onToggleFlip={this.onToggleFlip}
        onClickClose={() => {
          if (notificationId) {
            dispatch.notification.delete({ notificationId });
          }
          close('/mpnotifications');
        }}
        onClickPrimaryAction={() => {
          this.onClickPrimaryAction({ notificationType: type, closeFn: close });
        }}
        onClickSecondaryAction={() => {
          this.onClickSecondaryAction({ notificationType: type, closeFn: close });
        }}
        onClickC2L={({ data: { c2l } }) => c2lHandler(c2l, history, navigate)}
        locale={language}
        license={license}
      />
    );
  }
}

const withNavigate = (Comp: FunctionComponent<Props> | ComponentClass<Props>) => (props: Props) =>
  <Comp {...props} navigate={useNavigate()} />;

const mapState = (rootState: RootState) => ({
  language: select.locale.get(rootState),
  license: select.account.getLicense(rootState)
});

const mapDispatch = ({ modal: { close, unshift } }: Dispatch) => ({
  close,
  unshift
});

export default compose<React.FC>(
  withNavigate,
  withResolver([ResolverTag.StarsWebSports]),
  // @ts-ignore https://github.com/DefinitelyTyped/DefinitelyTyped/issues/31363
  connect<StateProps, DispatchProps>(mapState, mapDispatch)
)(StarsMPNotification);
